import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  CalendarDays, ImagePlus, LayoutTemplate, ListPlus, MapPin, Minus,
  Plus, Sparkles, Ticket, UploadCloud,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_POSTERS = {
  Navratri: "/assets/default-navratri.svg",
  Garba: "/assets/default-navratri.svg",
  Music: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1400&q=80",
  Comedy: "https://images.unsplash.com/photo-1527224857830-43a7acc85260?w=1400&q=80",
  Festivals: "https://images.unsplash.com/photo-1532375810709-75b1da00537c?w=1400&q=80",
  Food: "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=1400&q=80",
  Spiritual: "https://images.unsplash.com/photo-1545389336-cf090694435e?w=1400&q=80",
  Theatre: "https://images.unsplash.com/photo-1503095396549-807759245b35?w=1400&q=80",
  Business: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1400&q=80",
  Workshops: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1400&q=80",
  Dance: "https://images.unsplash.com/photo-1535525153412-5a092d46bd13?w=1400&q=80",
  default: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1400&q=80",
};

const TEMPLATES = {
  tiered: {
    label: "Tiered tickets",
    hint: "Best for concerts, comedy, mela, spiritual events and standard paid entry.",
    prices: [
      { tier: "VIP", price: 75, capacity: 100, description: "Premium entry or closest area" },
      { tier: "General", price: 35, capacity: 400, description: "Standard admission" },
      { tier: "Balcony", price: 20, capacity: 200, description: "Lower-cost area" },
    ],
  },
  "festival-zones": {
    label: "Festival zones",
    hint: "Use for Garba, Diwali, food festivals and family events with zones.",
    prices: [
      { tier: "Early Bird Entry", price: 18, capacity: 250, description: "Entry before peak time" },
      { tier: "General Entry", price: 28, capacity: 600, description: "Main event access" },
      { tier: "Family Pack", price: 95, capacity: 120, description: "Bundle for 4 guests" },
      { tier: "VIP Table", price: 240, capacity: 20, description: "Reserved table or premium area" },
    ],
  },
  "group-tables": {
    label: "Tables and groups",
    hint: "Use for wedding shows, networking dinners, awards nights and banquets.",
    prices: [
      { tier: "Single Seat", price: 45, capacity: 120, description: "One attendee" },
      { tier: "Table of 6", price: 240, capacity: 30, description: "Reserved group table" },
      { tier: "Sponsor Table", price: 500, capacity: 8, description: "Premium table with branding" },
    ],
  },
  free: {
    label: "Free registration",
    hint: "Use for community meets, mandir events, workshops or RSVP-only events.",
    prices: [
      { tier: "Free RSVP", price: 0, capacity: 300, description: "General registration" },
    ],
  },
  workshop: {
    label: "Workshop sessions",
    hint: "Use for classes, training, wellness sessions and limited-capacity learning.",
    prices: [
      { tier: "Standard Pass", price: 30, capacity: 60, description: "Main workshop admission" },
      { tier: "Materials Included", price: 45, capacity: 40, description: "Admission plus kit/materials" },
      { tier: "VIP Mentor Seat", price: 90, capacity: 10, description: "Small group mentor access" },
    ],
  },
  donation: {
    label: "Donation entry",
    hint: "Use for mandir, charity, community fundraisers and cultural causes.",
    prices: [
      { tier: "Free Entry", price: 0, capacity: 300, description: "Open community registration" },
      { tier: "Supporter", price: 11, capacity: 200, description: "Suggested donation" },
      { tier: "Family Donor", price: 51, capacity: 80, description: "Family contribution" },
    ],
  },
  expo: {
    label: "Expo and stalls",
    hint: "Use for business expos, wedding fairs, vendor events and community markets.",
    prices: [
      { tier: "Visitor Pass", price: 10, capacity: 800, description: "General visitor entry" },
      { tier: "Vendor Stall", price: 150, capacity: 40, description: "Standard exhibitor stall" },
      { tier: "Premium Stall", price: 300, capacity: 12, description: "High visibility stall" },
    ],
  },
  "multi-day-pass": {
    label: "Multi-day passes",
    hint: "Use for festivals, conferences, camps and events running over several days.",
    prices: [
      { tier: "Full Festival Pass", price: 110, capacity: 150, description: "All days plus priority entry" },
      { tier: "Day Pass", price: 25, capacity: 500, description: "Single-day access" },
      { tier: "Weekend Pass", price: 60, capacity: 300, description: "All public sessions" },
    ],
  },
};

const NAVRATRI_DEMO = {
  title: "Navratri Garba Night 2026 - Wembley",
  description: "A family friendly Navratri celebration with live garba, dandiya, food stalls, aarti, kids area and dedicated group entry lanes.",
  category: "Festivals",
  festival_campaign: "navratri-2026",
  language: "Gujarati",
  date: "2026-09-19",
  time: "18:30",
  end_date: "2026-09-21",
  end_time: "23:30",
  schedule_notes: "Three evening sessions across the opening Navratri weekend.",
  venue: "Wembley Community Arena",
  city: "London",
  venue_address: "Wembley Park, London",
  poster: "/assets/default-navratri.svg",
  ticketing_mode: "namaste",
  ticketing_approved: false,
  external_ticket_url: "",
  inquiry_phone: "",
  inquiry_whatsapp: "",
  inquiry_email: "",
  ticket_template: "festival-zones",
  ticket_notes: "Children under 5 enter free. Dandiya sticks available at the venue while stocks last.",
  min_per_order: 1,
  max_per_order: 10,
  layout_file: "https://images.unsplash.com/photo-1532375810709-75b1da00537c?w=1400&q=80",
  layout_file_name: "navratri-wembley-zone-layout.jpg",
  layout_file_type: "image/jpeg",
  layout_notes: "Demo layout: ground floor garba circle, VIP table zone, food stalls and family seating.",
};

const priceRowKey = (p, index) => p.ticket_key || p.id || `${p.tier || "tier"}-${index}-${Math.random().toString(36).slice(2, 8)}`;
const normalizePrices = (rows) => rows.map((p, index) => ({
  ...p,
  ticket_key: priceRowKey(p, index),
  scope: p.scope || inferTicketScope(p.tier),
  valid_dates: isAllEventDaysPass(p.tier) ? [] : (Array.isArray(p.valid_dates) ? p.valid_dates : []),
  scan_policy: p.scan_policy || ((p.scope || inferTicketScope(p.tier)) === "multi_day" ? "once_per_day" : "once"),
}));

export default function CreateEvent() {
  const { user } = useAuth();
  const { languages, categories, cities, campaigns } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Music",
    festival_campaign: "",
    language: "Gujarati",
    date: "",
    time: "19:30",
    end_date: "",
    end_time: "",
    schedule_notes: "",
    venue: "",
    venue_address: "",
    city: "London",
    poster: "",
    ticketing_mode: "namaste",
    ticketing_approved: false,
    external_ticket_url: "",
    inquiry_phone: "",
    inquiry_whatsapp: "",
    inquiry_email: "",
    ticket_template: "tiered",
    ticket_notes: "",
    min_per_order: 1,
    max_per_order: 8,
    layout_file: "",
    layout_file_name: "",
    layout_file_type: "",
    layout_notes: "",
  });
  const [prices, setPrices] = useState(normalizePrices(TEMPLATES.tiered.prices));
  const [ticketTemplates, setTicketTemplates] = useState(TEMPLATES);
  const [defaultPosters, setDefaultPosters] = useState(DEFAULT_POSTERS);
  const [submitting, setSubmitting] = useState(false);
  const [multiDay, setMultiDay] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get("/app-options/ticket_templates"),
      api.get("/app-options/default_posters"),
    ]).then(([templatesRes, postersRes]) => {
      if (templatesRes.status === "fulfilled" && templatesRes.value.data?.value) {
        setTicketTemplates(templatesRes.value.data.value);
      }
      if (postersRes.status === "fulfilled" && postersRes.value.data?.value) {
        setDefaultPosters(postersRes.value.data.value);
      }
    });
  }, []);

  useEffect(() => {
    if (!editId || !user) return;
    api.get(`/events/${editId}`).then((r) => {
      const event = r.data || {};
      if (event.organizer_id && event.organizer_id !== user.user_id && user.role !== "admin") {
        toast.error("You can only edit your own events.");
        navigate("/my-events");
        return;
      }
      setForm((current) => ({
        ...current,
        title: event.title || "",
        description: event.description || "",
        category: event.category || "Music",
        festival_campaign: event.festival_campaign || "",
        language: event.language || "Gujarati",
        date: event.date || "",
        time: event.time || "19:30",
        end_date: event.end_date || "",
        end_time: event.end_time || "",
        schedule_notes: event.schedule_notes || "",
        venue: event.venue || "",
        venue_address: event.venue_address || "",
        city: event.city || "London",
        poster: event.poster || event.poster_url || "",
        ticketing_mode: event.ticketing_mode || "namaste",
        ticketing_approved: false,
        external_ticket_url: event.external_ticket_url || "",
        inquiry_phone: event.inquiry_phone || "",
        inquiry_whatsapp: event.inquiry_whatsapp || "",
        inquiry_email: event.inquiry_email || "",
        ticket_template: event.ticket_template || "tiered",
        ticket_notes: event.ticket_notes || "",
        min_per_order: event.min_per_order || 1,
        max_per_order: event.max_per_order || 8,
        layout_file: event.layout_file || "",
        layout_file_name: event.layout_file_name || "",
        layout_file_type: event.layout_file_type || "",
        layout_notes: event.layout_notes || "",
      }));
      setPrices(normalizePrices(event.prices?.length ? event.prices : TEMPLATES.tiered.prices));
      setMultiDay(Boolean(event.end_date));
    }).catch(() => {
      toast.error("Could not load event for editing.");
      navigate("/my-events");
    });
  }, [editId, navigate, user]);

  const capacity = useMemo(() => prices.reduce((sum, p) => sum + (Number(p.capacity) || 0), 0), [prices]);
  const lowestPrice = useMemo(() => Math.min(...prices.map((p) => Number(p.price) || 0)), [prices]);
  const eventDateOptions = useMemo(() => buildDateOptions(form.date, multiDay ? form.end_date : ""), [form.date, form.end_date, multiDay]);
  const campaignOptions = useMemo(() => {
    const rows = Array.isArray(campaigns) ? campaigns : [];
    if (form.festival_campaign && !rows.some((item) => item.key === form.festival_campaign)) {
      return [{ key: form.festival_campaign, label: form.festival_campaign, active: true }, ...rows];
    }
    return rows;
  }, [campaigns, form.festival_campaign]);
  const defaultPosterForForm = () => {
    const text = `${form.title} ${form.category}`.toLowerCase();
    if (/(navratri|garba|dandiya|raas)/.test(text)) return defaultPosters.Navratri || DEFAULT_POSTERS.Navratri;
    return defaultPosters[form.category] || defaultPosters.default || DEFAULT_POSTERS.default;
  };

  const setTemplate = (template) => {
    setForm({ ...form, ticket_template: template });
    setPrices(normalizePrices(ticketTemplates[template]?.prices || TEMPLATES.tiered.prices));
  };

  const resizeImage = (file, maxWidth = 1600, maxHeight = 1000, quality = 0.82) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#0f1117";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const uploadDataUrl = async (file, onDone, options = {}) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Please upload a file under 8 MB.");
      return;
    }
    if (file.type.startsWith("image/")) {
      const result = await resizeImage(file, options.maxWidth, options.maxHeight, options.quality);
      onDone(result);
      toast.success("Image resized for web display");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onDone(reader.result);
    reader.readAsDataURL(file);
  };

  const fillNavratriDemo = () => {
    setForm({ ...form, ...NAVRATRI_DEMO });
    setPrices(normalizePrices(ticketTemplates["festival-zones"]?.prices || TEMPLATES["festival-zones"].prices));
    setMultiDay(true);
    toast.success("Navratri demo loaded");
  };

  const updatePrice = (index, key, value) => {
    const next = [...prices];
    next[index] = {
      ...next[index],
      [key]: key === "price" || key === "capacity" ? Number(value) : value,
    };
    if (key === "tier") {
      next[index].scope = inferTicketScope(value);
      next[index].scan_policy = next[index].scope === "multi_day" ? "once_per_day" : "once";
      if (isAllEventDaysPass(value)) {
        next[index].valid_dates = [];
      }
    }
    if (key === "scope") {
      next[index].scan_policy = value === "multi_day" ? "once_per_day" : "once";
      if (value === "single_day" && next[index].valid_dates?.length > 1) {
        next[index].valid_dates = next[index].valid_dates.slice(0, 1);
      }
    }
    setPrices(next);
  };

  const addTier = () => {
    setPrices([...prices, ...normalizePrices([{ tier: "New tier", price: 0, capacity: 50, description: "" }])]);
  };

  const removeTier = (index) => {
    setPrices(prices.filter((_, i) => i !== index));
  };

  const toggleTierDate = (index, date) => {
    const next = [...prices];
    const current = Array.isArray(next[index].valid_dates) ? next[index].valid_dates : [];
    const exists = current.includes(date);
    const scope = next[index].scope || "single_day";
    next[index] = {
      ...next[index],
      valid_dates: exists
        ? current.filter((day) => day !== date)
        : scope === "single_day"
          ? [date]
          : [...current, date],
    };
    setPrices(next);
  };

  const createDayPassRows = () => {
    if (!eventDateOptions.length) {
      toast.error("Add event start/end dates first.");
      return;
    }
    const base = prices.find((p) => /day pass/i.test(p.tier)) || prices[0] || { tier: "Day Pass", price: 0, capacity: 100, description: "Single-day access" };
    const rows = eventDateOptions.map((day) => ({
      ...base,
      ticket_key: `day-pass-${day.value}-${Math.random().toString(36).slice(2, 6)}`,
      tier: base.tier || "Day Pass",
      valid_dates: [day.value],
      scope: "single_day",
      scan_policy: "once",
      description: `${base.description || "Single-day access"} (${day.label})`,
    }));
    setPrices(rows);
    toast.success("Created one Day Pass row for each event day.");
  };

  const onSubmit = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin" && (!user.email_verified || !user.mobile)) {
      toast.error("Please verify your email and save a mobile contact number in Profile before creating an event.");
      return;
    }
    if (!form.title || !form.date || !form.venue) {
      toast.error("Please fill title, date and venue.");
      return;
    }
    const invalidPrices = prices.some((p) => {
      const capacity = Number(p.capacity);
      return !p.tier || Number(p.price) < 0 || capacity < 0 || (form.ticketing_mode === "namaste" && capacity <= 0);
    });
    if (!prices.length || invalidPrices) {
      toast.error("Please check ticket tier names, prices and capacities.");
      return;
    }
    if (multiDay && (!form.end_date || form.end_date < form.date)) {
      toast.error("Please add a valid end date for the multi-day event.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        poster: form.poster || defaultPosterForForm(),
        ticketing_mode: form.ticketing_mode,
        ticketing_approved: false,
        external_ticket_url: form.external_ticket_url,
        inquiry_phone: form.inquiry_phone,
        inquiry_whatsapp: form.inquiry_whatsapp,
        inquiry_email: form.inquiry_email,
        end_date: multiDay ? form.end_date : null,
        end_time: multiDay ? form.end_time : null,
        min_per_order: Number(form.min_per_order) || 1,
        max_per_order: Number(form.max_per_order) || 8,
        prices: normalizePrices(prices).map((p) => ({
          ...p,
          valid_dates: Array.isArray(p.valid_dates) ? p.valid_dates.filter(Boolean) : [],
          scan_policy: (p.scope || "single_day") === "multi_day" ? "once_per_day" : "once",
        })),
      };
      const { data } = editId ? await api.put(`/events/${editId}`, payload) : await api.post("/events", payload);
      toast.success(editId ? "Event resubmitted for review" : data.status === "approved" ? "Event created" : "Submitted for approval");
      navigate(`/organiser/${data.event_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md p-10 text-center">
          <h2 className="font-display text-2xl font-bold">Sign in to create an event</h2>
          <Button onClick={() => navigate("/login")} className="mt-4 rounded-full">Sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="page-shell py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-black text-primary">Organize</p>
            <h1 className="mt-2 font-display text-3xl font-black md:text-5xl">{editId ? "Amend your event" : "Create your event"}</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {editId ? "Update the requested details and resubmit for admin review." : "Build flexible event tickets, upload a venue layout reference, and submit for admin approval."}
            </p>
          </div>
          <Button variant="outline" onClick={fillNavratriDemo} className="rounded-full" data-testid="navratri-demo-btn">
            <Sparkles className="mr-2 h-4 w-4" />
            Load Navratri Demo
          </Button>
        </div>

        <div className="mt-8 grid gap-6 xl:grid-cols-[1fr_360px]">
          <section className="space-y-6">
            <Panel title="Event Details" icon={CalendarDays}>
              <div className="space-y-4">
                <Field label="Title">
                  <Input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </Field>
                <Field label="Description">
                  <Textarea data-testid="event-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </Field>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Category">
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Festival / campaign">
                    <Select value={form.festival_campaign || "none"} onValueChange={(v) => setForm({ ...form, festival_campaign: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {campaignOptions.map((campaign) => (
                          <SelectItem key={campaign.key} value={campaign.key}>{campaign.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Language">
                    <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {languages.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Date">
                    <Input type="date" data-testid="event-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  </Field>
                  <Field label="Time">
                    <Input type="time" data-testid="event-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                  </Field>
                  <div className="md:col-span-2 rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold">Multi-day event</p>
                        <p className="text-xs text-muted-foreground">Use when a festival, expo, camp or conference runs across more than one day.</p>
                      </div>
                      <Switch checked={multiDay} onCheckedChange={setMultiDay} />
                    </div>
                    {multiDay && (
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <Field label="End date">
                          <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                        </Field>
                        <Field label="End time">
                          <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
                        </Field>
                        <div className="md:col-span-2">
                          <Field label="Schedule notes">
                            <Input value={form.schedule_notes} onChange={(e) => setForm({ ...form, schedule_notes: e.target.value })} placeholder="Example: Friday preview, Saturday full day, Sunday family session" />
                          </Field>
                        </div>
                      </div>
                    )}
                  </div>
                  <Field label="Venue">
                    <Input data-testid="event-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
                  </Field>
                  <Field label="Venue address">
                    <Input value={form.venue_address} onChange={(e) => setForm({ ...form, venue_address: e.target.value })} placeholder="Street, area or postcode" />
                  </Field>
                  <Field label="City">
                    <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
            </Panel>

            <Panel title="Ticketing Control" icon={Ticket}>
              <div className="grid gap-3 md:grid-cols-3">
                {[
                  ["namaste", "Namaste ticketing", "Sell through our checkout and issue QR tickets. Namaste collects payment unless your Stripe account is connected for automatic payouts."],
                  ["external", "External ticket link", "Show the event and send users to organiser, Fatsoma, Eventbrite or another link."],
                  ["inquiry", "Inquiry only", "Show contact details while admin confirms ticketing or organiser setup."],
                ].map(([value, label, hint]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm({ ...form, ticketing_mode: value })}
                    className={`rounded-lg border p-4 text-left transition ${form.ticketing_mode === value ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
                  >
                    <p className="font-bold">{label}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
                  </button>
                ))}
              </div>
              {form.ticketing_mode === "external" && (
                <div className="mt-4">
                  <Field label="External ticket URL">
                    <Input value={form.external_ticket_url} onChange={(e) => setForm({ ...form, external_ticket_url: e.target.value })} placeholder="https://..." />
                  </Field>
                </div>
              )}
              {form.ticketing_mode === "namaste" && (
                <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4">
                  <Field label="Backup organiser payment URL (optional)">
                    <Input value={form.external_ticket_url} onChange={(e) => setForm({ ...form, external_ticket_url: e.target.value })} placeholder="https://organiser-ticket-link.example" />
                  </Field>
                  <p className="mt-2 text-xs text-muted-foreground">
                    No Stripe account is required to start. Namaste can collect payment and settle your balance manually, or you can connect Stripe for automatic payouts.
                  </p>
                </div>
              )}
              {form.ticketing_mode === "inquiry" && (
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <Field label="Inquiry phone">
                    <Input value={form.inquiry_phone} onChange={(e) => setForm({ ...form, inquiry_phone: e.target.value })} placeholder="+44..." />
                  </Field>
                  <Field label="WhatsApp number">
                    <Input value={form.inquiry_whatsapp} onChange={(e) => setForm({ ...form, inquiry_whatsapp: e.target.value })} placeholder="+44..." />
                  </Field>
                  <Field label="Inquiry email">
                    <Input value={form.inquiry_email} onChange={(e) => setForm({ ...form, inquiry_email: e.target.value })} placeholder="tickets@example.com" />
                  </Field>
                </div>
              )}
              <p className="mt-4 text-xs text-muted-foreground">
                Admin will approve the event and confirm whether NamasteNetwork should sell tickets before checkout goes live.
              </p>
            </Panel>

            <Panel title="Ticket Template" icon={LayoutTemplate}>
              <div className="grid gap-3 md:grid-cols-2">
                {Object.entries(ticketTemplates).map(([key, template]) => {
                  const active = form.ticket_template === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTemplate(key)}
                      className={`rounded-lg border p-4 text-left transition ${active ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
                    >
                      <p className="font-bold">{template.label}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{template.hint}</p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold">Pricing tiers</p>
                    <p className="text-xs text-muted-foreground">These are ticket categories, not fixed theatre seats.</p>
                  </div>
                  <Button type="button" variant="outline" onClick={addTier} className="rounded-full">
                    <ListPlus className="mr-2 h-4 w-4" />
                    Add tier
                  </Button>
                  {multiDay && (
                    <Button type="button" variant="outline" onClick={createDayPassRows} className="rounded-full">
                      <Plus className="mr-2 h-4 w-4" />
                      Day price rows
                    </Button>
                  )}
                </div>
                {prices.map((p, i) => (
                  <div key={p.ticket_key || `${p.tier}-${i}`} className="rounded-lg border border-border p-3">
                    <div className="grid gap-2 md:grid-cols-[1.2fr_0.7fr_0.8fr_1fr_1.4fr_auto]">
                      <Input value={p.tier} onChange={(e) => updatePrice(i, "tier", e.target.value)} placeholder="Tier name" />
                      <Input type="number" min="0" value={p.price} onChange={(e) => updatePrice(i, "price", e.target.value)} placeholder="Price" />
                      <Input type="number" min={form.ticketing_mode === "namaste" ? "1" : "0"} value={p.capacity} onChange={(e) => updatePrice(i, "capacity", e.target.value)} placeholder="Capacity" />
                      <Select value={p.scope || "single_day"} onValueChange={(value) => updatePrice(i, "scope", value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single_day">Single day</SelectItem>
                          <SelectItem value="multi_day">Multi-day pass</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input value={p.description || ""} onChange={(e) => updatePrice(i, "description", e.target.value)} placeholder="Short note" />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeTier(i)} disabled={prices.length === 1}>
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>
                    {multiDay && eventDateOptions.length > 1 && isAllEventDaysPass(p.tier) && (
                      <p className="mt-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs font-bold text-muted-foreground">
                        Full festival pass includes every event day automatically.
                      </p>
                    )}
                    {multiDay && eventDateOptions.length > 1 && !isAllEventDaysPass(p.tier) && (
                      <div className="mt-3">
                        <p className="mb-2 text-[11px] font-black uppercase tracking-[0.16em] text-muted-foreground">
                          Valid dates {p.scope === "single_day" ? "(choose one)" : "(choose included dates)"}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {eventDateOptions.map((day) => {
                            const active = (p.valid_dates || []).includes(day.value);
                            return (
                              <button
                                key={day.value}
                                type="button"
                                onClick={() => toggleTierDate(i, day.value)}
                                className={`rounded-full border px-3 py-1.5 text-xs font-bold ${active ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"}`}
                              >
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <Field label="Min per order">
                  <Input type="number" min="1" value={form.min_per_order} onChange={(e) => setForm({ ...form, min_per_order: e.target.value })} />
                </Field>
                <Field label="Max per order">
                  <Input type="number" min="1" value={form.max_per_order} onChange={(e) => setForm({ ...form, max_per_order: e.target.value })} />
                </Field>
                <Field label="Ticket notes">
                  <Input value={form.ticket_notes} onChange={(e) => setForm({ ...form, ticket_notes: e.target.value })} placeholder="Age rules, entry rules, etc." />
                </Field>
              </div>
            </Panel>

            <Panel title="Media And Layout" icon={UploadCloud}>
              <div className="grid gap-5 lg:grid-cols-2">
                <UploadBox
                  label="Poster"
                  accept="image/*"
                  value={form.poster || DEFAULT_POSTERS[form.category] || DEFAULT_POSTERS.default}
                  fileName={form.poster ? "poster image" : ""}
                  icon={ImagePlus}
                  onFile={(file) => uploadDataUrl(file, (result) => setForm({ ...form, poster: result }), { maxWidth: 1600, maxHeight: 1000 })}
                />
                <UploadBox
                  label="Venue layout file"
                  accept="image/*,application/pdf"
                  value={form.layout_file}
                  fileName={form.layout_file_name}
                  icon={MapPin}
                  onFile={(file) => uploadDataUrl(file, (result) => setForm({
                    ...form,
                    layout_file: result,
                    layout_file_name: file.name,
                    layout_file_type: file.type,
                  }), { maxWidth: 1800, maxHeight: 1400 })}
                />
              </div>
              <div className="mt-4">
                <Field label="Layout notes">
                  <Textarea
                    rows={3}
                    value={form.layout_notes}
                    onChange={(e) => setForm({ ...form, layout_notes: e.target.value })}
                    placeholder="Example: VIP tables near stage, general entry through Gate B, food stalls behind main hall."
                  />
                </Field>
              </div>
            </Panel>
          </section>

          <aside className="space-y-4 xl:sticky xl:top-20 xl:h-fit">
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Preview</p>
              <div className="mt-4 overflow-hidden rounded-lg border border-border">
                {form.poster ? (
                  <img src={form.poster} alt="" className="aspect-[16/10] w-full object-cover" />
                ) : defaultPosterForForm() ? (
                  <img src={defaultPosterForForm()} alt="" className="aspect-[16/10] w-full object-cover" />
                ) : (
                  <div className="grid aspect-[16/10] place-items-center bg-muted text-sm text-muted-foreground">Poster preview</div>
                )}
              </div>
              <h2 className="mt-4 font-display text-2xl font-black">{form.title || "Untitled event"}</h2>
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <p>{formatSchedule(form, multiDay)}</p>
                <p>{form.venue || "Venue TBC"}, {form.city}</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Badge variant="outline">{form.category}</Badge>
                {form.festival_campaign && <Badge variant="outline">{form.festival_campaign}</Badge>}
                <Badge variant="outline">{form.language}</Badge>
                <Badge variant="outline">{TEMPLATES[form.ticket_template]?.label}</Badge>
                <Badge variant="outline">{form.ticketing_mode === "namaste" ? "Namaste ticketing" : form.ticketing_mode === "external" ? "External tickets" : "Inquiry only"}</Badge>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
              <p className="flex items-center gap-2 text-sm font-bold"><Ticket className="h-4 w-4 text-primary" /> Ticket summary</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Summary label="Capacity" value={capacity} />
                <Summary label="From" value={`£${Number(lowestPrice || 0).toFixed(2)}`} />
              </div>
              <div className="mt-4 space-y-2">
                {prices.map((p, i) => (
                  <div key={`${p.tier}-summary-${i}`} className="flex justify-between gap-3 text-sm">
                    <span className="line-clamp-1 text-muted-foreground">{p.tier}</span>
                    <span className="font-bold">£{Number(p.price || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={onSubmit} disabled={submitting} className="h-12 w-full rounded-full font-bold" data-testid="event-submit-btn">
              {submitting ? "Submitting..." : editId ? "Resubmit Event" : "Submit Event"}
            </Button>
            <p className="px-2 text-center text-xs text-muted-foreground">
              Organiser submissions go to admin approval before appearing publicly.
            </p>
          </aside>
        </div>
      </main>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <div className="mb-5 flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <h2 className="font-display text-xl font-black">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold">{label}</span>
      {children}
    </label>
  );
}

function UploadBox({ label, accept, value, fileName, icon: Icon, onFile }) {
  const isImage = value && (
    value.startsWith("data:image/") ||
    (value.startsWith("http") && !String(value).toLowerCase().includes(".pdf")) ||
    /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(fileName || value)
  );
  return (
    <div>
      <p className="mb-2 text-xs font-bold">{label}</p>
      <label className="block cursor-pointer rounded-lg border border-dashed border-border p-4 transition hover:border-primary/60">
        <input type="file" accept={accept} onChange={(e) => onFile(e.target.files?.[0])} className="sr-only" />
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-bold">{fileName || "Choose file"}</p>
            <p className="text-xs text-muted-foreground">Images auto-resize; PDF accepted for layouts</p>
          </div>
        </div>
      </label>
      {value && (
        <div className="mt-3 overflow-hidden rounded-lg border border-border">
          {isImage ? (
            <img src={value} alt="" className="h-36 w-full object-cover" />
          ) : (
            <div className="grid h-24 place-items-center bg-muted text-sm font-bold">PDF layout attached</div>
          )}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-xl font-black">{value}</p>
    </div>
  );
}

function formatSchedule(form, multiDay) {
  if (!form.date) return `Date TBC at ${form.time}`;
  if (multiDay && form.end_date) {
    return `${form.date} ${form.time} to ${form.end_date} ${form.end_time || ""}`.trim();
  }
  return `${form.date} at ${form.time}`;
}

function inferTicketScope(name) {
  const label = String(name || "").toLowerCase();
  return /(weekend|full|festival pass|festive pass|season|all days|all-day|all day)/.test(label) ? "multi_day" : "single_day";
}

function isAllEventDaysPass(name) {
  const label = String(name || "").toLowerCase();
  return /(full|festival pass|festive pass|season|all days|all-day|all day)/.test(label);
}

function buildDateOptions(start, end) {
  if (!start) return [];
  const startDate = new Date(`${start}T12:00:00`);
  const endDate = new Date(`${end || start}T12:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
    return [{ value: start, label: start }];
  }
  const days = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate && days.length < 31) {
    days.push({
      value: cursor.toISOString().slice(0, 10),
      label: cursor.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}
